import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { Panel, ActionButton, TextField, LocalizedField } from "./_shared";
import BlocksEditor from "./BlocksEditor";
import ImageUploader from "@/components/ImageUploader";
import IconPicker from "@/components/IconPicker";
import { Plus, Save, Trash2, Star, X } from "lucide-react";
import { toast } from "sonner";

/** Preset tag chips per common categories. Free-form typing is also allowed. */
const TAG_PRESETS = [
  "Spiaggia libera",
  "Spiaggia attrezzata",
  "Bar",
  "Ristorante",
  "Parcheggio",
  "Adatta ai bambini",
  "Cani ammessi",
  "Docce",
  "Noleggio ombrelloni",
  "Accessibile",
];

/** Tag editor with preset chips + free-form input. Values are plain strings. */
function TagsEditor({ value = [], onChange }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];
  const add = (tag) => {
    const t = (tag || "").trim();
    if (!t) return;
    if (list.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onChange([...list, t]);
    setDraft("");
  };
  const remove = (tag) => onChange(list.filter((x) => x !== tag));
  const notYetAdded = TAG_PRESETS.filter(
    (p) => !list.some((x) => x.toLowerCase() === p.toLowerCase())
  );
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-500">
        Servizi / caratteristiche
      </label>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="poi-tags-list">
        {list.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-bronze-50 text-bronze-500 border border-bronze-200/60 text-xs px-2.5 py-1"
            data-testid={`poi-tag-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="hover:text-red-600"
              aria-label={`Rimuovi ${t}`}
              data-testid={`poi-tag-remove-${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {list.length === 0 && (
          <span className="text-xs text-ink-300">Nessun tag. Aggiungine sotto.</span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder="Aggiungi tag e premi Invio (es. Cani ammessi)"
          className="flex-1 h-9 bg-white border border-ink-200/60 rounded-md px-3 text-sm"
          data-testid="poi-tag-input"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          className="inline-flex items-center gap-1 rounded-full bg-cream-100 hover:bg-corten-50 text-ink-700 hover:text-corten-500 text-xs px-3 py-1.5 transition"
          data-testid="poi-tag-add"
        >
          <Plus className="w-3 h-3" /> Aggiungi
        </button>
      </div>
      {notYetAdded.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.16em] text-ink-400 self-center mr-1">
            Suggerimenti
          </span>
          {notYetAdded.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => add(p)}
              className="text-xs rounded-full border border-ink-200/60 text-ink-500 hover:text-bronze-500 hover:border-bronze-200 px-2.5 py-1 transition"
              data-testid={`poi-tag-preset-${p}`}
            >
              + {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const empty = (pid, catId) => ({
  property_id: pid,
  category_id: catId || "",
  category_ids: catId ? [catId] : [],
  title: { it: "", en: "", de: "" },
  subtitle: { it: "", en: "", de: "" },
  description: { it: "", en: "", de: "" },
  images: [],
  icon: null,
  address: "", lat: null, lng: null,
  phone: "", website: "", email: "",
  hours: { it: "", en: "", de: "" },
  notes: { it: "", en: "", de: "" },
  tags: [],
  featured: false,
  order: 99,
  blocks: [],
});

export default function POIManager() {
  const [propertyId, setPropertyId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async (pid, catId) => {
    const [pRes, cRes, pois] = await Promise.all([
      api.get("/admin/properties"),
      api.get(`/admin/categories${pid ? `?property_id=${pid}` : ""}`),
      api.get(`/admin/pois${catId ? `?category_id=${catId}` : ""}`),
    ]);
    setProperties(pRes.data || []);
    setCategories(cRes.data || []);
    setItems(pois.data || []);
    if (!pid && pRes.data?.length) setPropertyId(pRes.data.find((p) => p.is_default)?.id || pRes.data[0].id);
  };
  useEffect(() => { load(propertyId, categoryFilter); }, [propertyId, categoryFilter]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name?.it || c.slug])), [categories]);

  const save = async () => {
    try {
      // Normalise: category_ids drives everything; category_id mirrors [0] for
      // legacy callers. Backend also normalises but do it here for clarity.
      const ids = (editing.category_ids || []).filter(Boolean);
      if (ids.length === 0) { toast.error("Seleziona almeno una categoria"); return; }
      const payload = { ...editing, category_ids: ids, category_id: ids[0] };
      if (editing.id) await api.patch(`/admin/pois/${editing.id}`, payload);
      else await api.post("/admin/pois", payload);
      toast.success("Salvato");
      setEditing(null);
      load(propertyId, categoryFilter);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo elemento?")) return;
    await api.delete(`/admin/pois/${id}`);
    load(propertyId, categoryFilter);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">Punti di interesse</h1>
          <p className="text-xs text-ink-500 mt-1">Contenuti delle categorie · CMS visuale, galleria, badge in evidenza</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={propertyId || ""} onChange={(e) => setPropertyId(e.target.value)} className="text-xs bg-white border border-ink-200/60 rounded-full px-3 py-2">
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-xs bg-white border border-ink-200/60 rounded-full px-3 py-2" data-testid="poi-category-filter">
            <option value="">Tutte le categorie</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name?.it || c.slug}</option>)}
          </select>
          <ActionButton onClick={() => setEditing(empty(propertyId, categoryFilter))} testid="poi-add"><Plus className="w-3.5 h-3.5" /> Nuovo POI</ActionButton>
        </div>
      </div>

      <Panel title="Elenco">
        <div className="divide-y divide-ink-200/30">
          {items.length === 0 && <p className="text-sm text-ink-300 py-6 text-center">Nessun POI. Aggiungine uno.</p>}
          {items.map((p) => (
            <div key={p.id} className="py-3 flex items-center justify-between gap-2" data-testid={`poi-row-${p.id}`}>
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  {p.featured && <Star className="w-3.5 h-3.5 text-corten-400" />}
                  <span className="truncate">{p.title?.it || p.title?.en || p.id}</span>
                </div>
                <div className="text-xs text-ink-500">
                  {(p.category_ids && p.category_ids.length > 0
                    ? p.category_ids
                    : [p.category_id]
                  )
                    .filter(Boolean)
                    .map((cid) => catMap[cid] || cid)
                    .join(" · ")}
                  {" · #"}{p.order}
                </div>
              </div>
              <div className="flex gap-2">
                <ActionButton variant="secondary" testid={`poi-edit-${p.id}`} onClick={() => setEditing({ ...p, category_ids: (p.category_ids && p.category_ids.length > 0) ? p.category_ids : (p.category_id ? [p.category_id] : []) })}>Modifica</ActionButton>
                <ActionButton variant="danger" testid={`poi-delete-${p.id}`} onClick={() => remove(p.id)}><Trash2 className="w-3.5 h-3.5" /></ActionButton>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {editing && (
        <Panel title={editing.id ? "Modifica POI" : "Nuovo POI"} action={
          <div className="flex gap-2">
            <ActionButton variant="secondary" onClick={() => setEditing(null)}>Annulla</ActionButton>
            <ActionButton onClick={save} testid="poi-save" disabled={uploading}>
              <Save className="w-3.5 h-3.5" /> {uploading ? "Caricamento..." : "Salva"}
            </ActionButton>
          </div>
        }>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-500">
                Categorie · seleziona una o più
              </label>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="poi-category-multiselect">
                {categories.map((c) => {
                  const selected = (editing.category_ids || []).includes(c.id);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => {
                        const list = editing.category_ids || [];
                        const next = selected
                          ? list.filter((x) => x !== c.id)
                          : [...list, c.id];
                        setEditing({
                          ...editing,
                          category_ids: next,
                          category_id: next[0] || "",
                        });
                      }}
                      data-testid={`poi-category-chip-${c.slug}`}
                      className={
                        "inline-flex items-center gap-1 rounded-full text-xs px-3 py-1.5 border transition " +
                        (selected
                          ? "bg-bronze-50 text-bronze-500 border-bronze-200"
                          : "bg-white text-ink-500 border-ink-200/60 hover:border-bronze-200 hover:text-bronze-500")
                      }
                    >
                      {selected ? "✓ " : "+ "}{c.name?.it || c.slug}
                    </button>
                  );
                })}
              </div>
              {(editing.category_ids || []).length > 1 && (
                <div className="mt-1.5 text-[11px] text-ink-400">
                  Il POI comparirà in {(editing.category_ids || []).length} categorie ma resta una sola scheda. La categoria "primaria" (usata come default) è la prima selezionata: <b>{catMap[(editing.category_ids || [])[0]]}</b>.
                </div>
              )}
            </div>
            <IconPicker value={editing.icon || ""} onChange={(v) => setEditing({ ...editing, icon: v || null })} label="Icona (opzionale)" testid="poi-icon-picker" />
            <TextField label="Indirizzo" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} />
            <TextField label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
            <TextField label="Latitudine" type="number" value={editing.lat} onChange={(v) => setEditing({ ...editing, lat: v })} />
            <TextField label="Longitudine" type="number" value={editing.lng} onChange={(v) => setEditing({ ...editing, lng: v })} />
            <TextField label="Telefono" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} />
            <TextField label="Sito web" value={editing.website} onChange={(v) => setEditing({ ...editing, website: v })} />
            <TextField label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="poi-featured"/>
              <span className="text-sm">In evidenza (badge ★)</span>
            </label>

            <div className="md:col-span-2"><LocalizedField label="Titolo" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="poi-title" /></div>
            <div className="md:col-span-2"><LocalizedField label="Sottotitolo" value={editing.subtitle} onChange={(v) => setEditing({ ...editing, subtitle: v })} /></div>
            <div className="md:col-span-2"><LocalizedField label="Descrizione" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} multiline /></div>
            <div className="md:col-span-2"><LocalizedField label="Orari" value={editing.hours} onChange={(v) => setEditing({ ...editing, hours: v })} multiline /></div>
            <div className="md:col-span-2"><LocalizedField label="Note" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} multiline /></div>

            <div className="md:col-span-2">
              <TagsEditor value={editing.tags || []} onChange={(v) => setEditing({ ...editing, tags: v })} />
            </div>

            <div className="md:col-span-2">
              <ImageUploader
                label="Galleria immagini"
                multiple
                value={editing.images || []}
                onChange={(v) => setEditing({ ...editing, images: v })}
                onBusyChange={setUploading}
                hint="La prima immagine è la copertina della card. Trascina per riordinare."
                testid="poi-images-uploader"
              />
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-editorial italic text-2xl mb-3">Contenuti aggiuntivi (CMS visuale)</h3>
            <BlocksEditor blocks={editing.blocks || []} onChange={(b) => setEditing({ ...editing, blocks: b })} />
          </div>
        </Panel>
      )}
    </div>
  );
}
